import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, withKeys as _withKeys, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "auth-page" }
const _hoisted_2 = { class: "auth-container" }
const _hoisted_3 = { class: "auth-card" }
const _hoisted_4 = { class: "name-row" }
const _hoisted_5 = {
  key: 0,
  class: "icon-container"
}
const _hoisted_6 = { class: "auth-footer" }

import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import type { FormInstance, FormRules } from 'element-plus';
import { ElMessage } from 'element-plus';
import {
  UserFilled,
  User,
  Avatar,
  Message,
  Lock,
} from '@element-plus/icons-vue';
import { useAuthStore } from '@/stores/auth';
import type { RegisterData } from '@/types/auth';


export default /*@__PURE__*/_defineComponent({
  __name: 'Register',
  setup(__props) {

const authStore = useAuthStore();
const router = useRouter();
const registerFormRef = ref<FormInstance>();

const registerForm = reactive({
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
});

// Custom validators
const validateUsername = async (rule: any, value: string, callback: any) => {
  if (!value) {
    return callback(new Error('Username is required'));
  }
  if (value.length < 3) {
    return callback(new Error('Username must be at least 3 characters'));
  }
  if (!/^[a-zA-Z0-9_]+$/.test(value)) {
    return callback(
      new Error('Username can only contain letters, numbers, and underscores')
    );
  }
  callback();
};

const validateEmail = (rule: any, value: string, callback: any) => {
  if (!value) {
    return callback(new Error('Email is required'));
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return callback(new Error('Please enter a valid email address'));
  }
  callback();
};

const validatePassword = (rule: any, value: string, callback: any) => {
  if (!value) {
    return callback(new Error('Password is required'));
  }
  if (value.length < 8) {
    return callback(new Error('Password must be at least 8 characters'));
  }
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
    return callback(
      new Error(
        'Password must contain at least one lowercase letter, one uppercase letter, and one number'
      )
    );
  }
  callback();
};

const validateConfirmPassword = (rule: any, value: string, callback: any) => {
  if (!value) {
    return callback(new Error('Please confirm your password'));
  }
  if (value !== registerForm.password) {
    return callback(new Error('Passwords do not match'));
  }
  callback();
};

const registerRules: FormRules = {
  firstName: [
    { required: true, message: 'First name is required', trigger: 'blur' },
    {
      min: 2,
      message: 'First name must be at least 2 characters',
      trigger: 'blur',
    },
  ],
  lastName: [
    { required: true, message: 'Last name is required', trigger: 'blur' },
    {
      min: 2,
      message: 'Last name must be at least 2 characters',
      trigger: 'blur',
    },
  ],
  username: [{ validator: validateUsername, trigger: 'blur' }],
  email: [{ validator: validateEmail, trigger: 'blur' }],
  password: [{ validator: validatePassword, trigger: 'blur' }],
  confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }],
};

const checkUsernameAvailability = async () => {
  if (registerForm.username.length >= 3) {
    const isAvailable = await authStore.checkUsernameAvailability(
      registerForm.username
    );
    if (!isAvailable) {
      ElMessage.warning('Username is already taken');
    }
  }
};

const checkEmailAvailability = async () => {
  if (
    registerForm.email &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerForm.email)
  ) {
    const isAvailable = await authStore.checkEmailAvailability(
      registerForm.email
    );
    if (!isAvailable) {
      ElMessage.warning('Email is already registered');
    }
  }
};

const handleRegister = async () => {
  if (!registerFormRef.value) {
    return;
  }

  try {
    await registerFormRef.value.validate();

    const userData: RegisterData = {
      firstName: registerForm.firstName.trim(),
      lastName: registerForm.lastName.trim(),
      username: registerForm.username.trim(),
      email: registerForm.email.trim(),
      password: registerForm.password,
    };

    const success = await authStore.register(userData);

    if (success) {
      // Redirect to login page
      router.push('/login');
    }
  } catch (error) {
    console.error('Registration validation failed:', error);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[8] || (_cache[8] = _createElementVNode("div", { class: "auth-header" }, [
          _createElementVNode("h1", null, "Join Recipe Vault"),
          _createElementVNode("p", null, "Create your account and start sharing recipes")
        ], -1)),
        _createVNode(_component_el_form, {
          ref_key: "registerFormRef",
          ref: registerFormRef,
          model: registerForm,
          rules: registerRules,
          class: "auth-form",
          size: "large",
          onSubmit: _withModifiers(handleRegister, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_el_form_item, {
                prop: "firstName",
                class: "name-field"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_input, {
                    modelValue: registerForm.firstName,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((registerForm.firstName) = $event)),
                    placeholder: "First Name",
                    "prefix-icon": "User",
                    disabled: _unref(authStore).isAuthenticating
                  }, null, 8, ["modelValue", "disabled"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_form_item, {
                prop: "lastName",
                class: "name-field"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_input, {
                    modelValue: registerForm.lastName,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((registerForm.lastName) = $event)),
                    placeholder: "Last Name",
                    "prefix-icon": "User",
                    disabled: _unref(authStore).isAuthenticating
                  }, null, 8, ["modelValue", "disabled"])
                ]),
                _: 1
              })
            ]),
            _createVNode(_component_el_form_item, { prop: "username" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: registerForm.username,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((registerForm.username) = $event)),
                  placeholder: "Username",
                  "prefix-icon": "Avatar",
                  disabled: _unref(authStore).isAuthenticating,
                  onBlur: checkUsernameAvailability
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, { prop: "email" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: registerForm.email,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((registerForm.email) = $event)),
                  type: "email",
                  placeholder: "Email Address",
                  "prefix-icon": "Message",
                  disabled: _unref(authStore).isAuthenticating,
                  onBlur: checkEmailAvailability
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, { prop: "password" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: registerForm.password,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((registerForm.password) = $event)),
                  type: "password",
                  placeholder: "Password",
                  "prefix-icon": "Lock",
                  "show-password": "",
                  disabled: _unref(authStore).isAuthenticating
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, { prop: "confirmPassword" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: registerForm.confirmPassword,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((registerForm.confirmPassword) = $event)),
                  type: "password",
                  placeholder: "Confirm Password",
                  "prefix-icon": "Lock",
                  "show-password": "",
                  disabled: _unref(authStore).isAuthenticating,
                  onKeyup: _withKeys(handleRegister, ["enter"])
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_button, {
                  type: "primary",
                  class: "custom-button auth-submit-btn",
                  loading: _unref(authStore).isAuthenticating,
                  onClick: handleRegister
                }, {
                  default: _withCtx(() => [
                    (!_unref(authStore).isAuthenticating)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
                          _createVNode(_unref(UserFilled), { class: "button-icon" })
                        ]))
                      : _createCommentVNode("", true),
                    _createTextVNode(" " + _toDisplayString(_unref(authStore).isAuthenticating
                  ? 'Creating Account...'
                  : 'Create Account'), 1)
                  ]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("p", null, [
            _cache[7] || (_cache[7] = _createTextVNode(" Already have an account? ")),
            _createVNode(_component_router_link, {
              to: "/login",
              class: "auth-link"
            }, {
              default: _withCtx(() => _cache[6] || (_cache[6] = [
                _createTextVNode("Sign in here")
              ])),
              _: 1,
              __: [6]
            })
          ])
        ])
      ])
    ])
  ]))
}
}

})